import posthog from "posthog-js";
import React, { useEffect, useMemo, useRef } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { months, monthToNumber } from "../../../handlers/utilityFunctions";
import { getJobFunctions } from "../../../seed/getMetadata";
import { useUserAccount } from "../../../services/create-resume/queries";
import Dropdown from "../components/Dropdown";
import OptionBadge from "../components/OptionBadge";
import AuthLayout from "../layout/AuthLayout";
import { clearPendingSignup, getPendingSignup, setPendingSignup, YesNo } from "../pendingSignup";
import { deriveSeniorityLevel } from "../seniority";

const MAX_JOB_TITLE_LENGTH = 100;

/** Graduating soon counts too, so the window runs a couple of years into the future. */
function graduationYears(): string[] {
	const thisYear = new Date().getFullYear();
	const years = [];
	for (let year = thisYear - 3; year <= thisYear + 2; year++) years.push(String(year));
	return years.reverse();
}

function careerStartYears(): string[] {
	const thisYear = new Date().getFullYear();
	const years = [];
	for (let year = 1980; year <= thisYear; year++) years.push(String(year));
	return years.reverse();
}

function OnboardingScreen() {
	const navigate = useNavigate();
	const pendingSignup = useMemo(() => getPendingSignup(), []);

	const [jobTitle, setJobTitle] = React.useState("");
	const [areasOfExpertise, setAreasOfExpertise] = React.useState<string[]>([]);
	const [recentGraduate, setRecentGraduate] = React.useState<YesNo | "">("");
	const [experienceMonth, setExperienceMonth] = React.useState("");
	const [experienceYear, setExperienceYear] = React.useState("");

	const answeredQuestions = useRef<Set<string>>(new Set());

	// A returning user signing back in with Google is sent here by the register form, which can't
	// tell an existing account from a new one. Disabled without an auth token, so the email-signup
	// path (not logged in until the address is verified) never waits on it.
	const { data: userAccount, isLoading: isLoadingAccount } = useUserAccount();
	const preferences = userAccount?.jobPreferences;
	const hasAnsweredOnboarding = Boolean(
		preferences?.desiredTitle || preferences?.areasOfExpertise?.length > 0 || preferences?.seniorityLevel,
	);

	function trackQuestionAnswered(question: string) {
		if (answeredQuestions.current.has(question)) return;
		answeredQuestions.current.add(question);
		posthog.capture("registration_question_answered", { question, step: "onboarding" });
	}

	useEffect(() => {
		if (!pendingSignup) return;
		posthog.capture("registration_onboarding_started", { method: pendingSignup.method });
	}, [pendingSignup]);

	// Rehydrate if the user came back to this step.
	useEffect(() => {
		if (!pendingSignup) return;
		setJobTitle(pendingSignup.jobTitle || "");
		setAreasOfExpertise(pendingSignup.areasOfExpertise || []);
		setRecentGraduate(pendingSignup.recentGraduate || "");
		setExperienceMonth(pendingSignup.experienceMonth || "");
		setExperienceYear(pendingSignup.experienceYear || "");
	}, [pendingSignup]);

	// Don't flash the questions at someone who is about to be bounced past them.
	if (isLoadingAccount) {
		return null;
	}

	if (hasAnsweredOnboarding) {
		// The register form wrote a draft on the way in; it would otherwise re-POST /users later.
		clearPendingSignup();
		return <Navigate to="/profile" replace />;
	}

	if (!pendingSignup) {
		return <Navigate to="/register" replace />;
	}

	const onSubmit = (e: React.FormEvent) => {
		e.preventDefault();

		if (!jobTitle.trim()) {
			toast.error("Please enter your desired job title");
			return;
		}

		if (areasOfExpertise.length === 0) {
			toast.error("Please select your area of expertise");
			return;
		}

		if (!recentGraduate) {
			toast.error("Please let us know if you are a recent graduate");
			return;
		}

		if (!experienceMonth || !experienceYear) {
			toast.error(
				recentGraduate === "Yes"
					? "Please select when you graduated or expect to graduate"
					: "Please select when you started your professional career",
			);
			return;
		}

		const seniorityLevel = deriveSeniorityLevel(monthToNumber(experienceMonth), Number(experienceYear));

		setPendingSignup({
			...pendingSignup,
			jobTitle: jobTitle.trim(),
			areasOfExpertise,
			recentGraduate,
			experienceMonth,
			experienceYear,
			seniorityLevel,
		});

		posthog.capture("registration_onboarding_completed", {
			method: pendingSignup.method,
			recent_graduate: recentGraduate,
			// The band isn't asked for any more, so record what we inferred.
			derived_seniority_level: seniorityLevel,
		});

		navigate("/creating-profile", { state: { method: pendingSignup.method } });
	};

	return (
		<AuthLayout>
			<div className="py-12">
				<div className="mb-6 flex flex-col items-center space-y-2">
					<h3 className="!font-bold text-[28px] leading-tight text-[#161616] !font-plus-jakarta mb-2 text-center">
						Welcome{pendingSignup.firstName ? `, ${pendingSignup.firstName}` : ""}!
					</h3>
					<p className="text-[#5C5C5C] text-sm text-center">
						Three quick questions so we can start matching you with the right jobs.
					</p>
				</div>

				<form onSubmit={onSubmit}>
					<div className="flex flex-col gap-1 mb-4">
						<label className="font-semibold text-sm text-gray-700">What&#39;s your dream job title?</label>
						<input
							type="text"
							className="border border-gray-200 h-9 text-sm placeholder:text-sm rounded px-2 py-1 placeholder:text-[#7A7A7A]"
							placeholder="Enter your desired job title"
							maxLength={MAX_JOB_TITLE_LENGTH}
							value={jobTitle}
							onChange={(e) => setJobTitle(e.target.value)}
							onBlur={(e) => {
								if (e.target.value.trim()) trackQuestionAnswered("job_title");
							}}
						/>
					</div>

					<div className="flex flex-col gap-1 mb-4">
						<label className="font-semibold text-sm text-gray-700">
							What&#39;s your superpower skill or area of expertise?
						</label>
						<Dropdown
							placeholder="Select all that apply"
							multiple
							options={[...getJobFunctions()].sort()}
							onChange={(val) => {
								if (areasOfExpertise.includes(val)) {
									setAreasOfExpertise(areasOfExpertise.filter((item) => item !== val));
								} else {
									setAreasOfExpertise([...areasOfExpertise, val]);
									trackQuestionAnswered("area_of_expertise");
								}
							}}
							value={areasOfExpertise}
						/>
					</div>

					<div className="flex flex-col gap-1 mb-6">
						<label className="font-semibold text-sm text-gray-700">Are you a recent graduate?</label>
						<div className="flex gap-3 mt-1 flex-wrap">
							{(["Yes", "No"] as YesNo[]).map((answer) => (
								<OptionBadge
									key={answer}
									value={answer}
									selected={recentGraduate === answer}
									onSelect={() => {
										if (answer !== recentGraduate) {
											// The two branches ask about different dates — don't carry one answer into the other.
											setExperienceMonth("");
											setExperienceYear("");
										}
										setRecentGraduate(answer);
										trackQuestionAnswered("recent_graduate");
									}}
								/>
							))}
						</div>

						{recentGraduate && (
							<>
								<label className="font-semibold text-sm text-gray-700 mt-3">
									{recentGraduate === "Yes"
										? "When did you graduate or expect to graduate?"
										: "When did you start your professional career?"}
								</label>
								<div className="grid grid-cols-2 gap-3">
									<Dropdown
										placeholder="Select Month"
										options={months}
										value={experienceMonth}
										onChange={(val) => {
											setExperienceMonth(val);
											trackQuestionAnswered(recentGraduate === "Yes" ? "graduation_date" : "career_start");
										}}
									/>
									<Dropdown
										placeholder="Select Year"
										options={recentGraduate === "Yes" ? graduationYears() : careerStartYears()}
										value={experienceYear}
										onChange={(val) => {
											setExperienceYear(val);
											trackQuestionAnswered(recentGraduate === "Yes" ? "graduation_date" : "career_start");
										}}
									/>
								</div>
							</>
						)}
					</div>

					<button
						className="w-full rounded-md bg-primary-700 text-white font-semibold font-inter text-base py-2 flex items-center justify-center"
						type="submit"
					>
						Continue
					</button>
				</form>
			</div>
		</AuthLayout>
	);
}

export default OnboardingScreen;
